import semver from 'semver';
import { BitePlatform, BitePlatformHelper, KioskDeviceType, KioskDeviceTypeHelper, KioskOs, MdmProvider, } from '@biteinc/enums';
import { VersioningHelper } from '@biteinc/helpers';
const LATEST_GA_ANDROID_V1_BUILD_VERSION = '1.19.9';
const LATEST_GA_ANDROID_V2_BUILD_VERSION = '2.11.2';
const LATEST_GA_IOS_BUILD_VERSION = '3.17';
// Don't specify the return type so that we can infer with ReturnType<> and use as an "enum" in
// deviceTypeFromModelName.
// oxlint-disable-next-line typescript/explicit-function-return-type
function nameFromIosModelName(modelName) {
    switch (modelName) {
        case 'iPad5,3':
            return 'iPad Air 2';
        case 'iPad6,7':
            return 'iPad Pro 12.9"';
        case 'iPad7,1':
        case 'iPad7,2': // This one has LTE too
            return 'iPad Pro 12.9" 2G';
        case 'iPad7,11':
            return 'iPad 10.9" 7G';
        case 'iPad8,5':
        case 'iPad8,6':
        case 'iPad8,7':
        case 'iPad8,8':
            return 'iPad Pro 12.9" 3G';
        case 'iPad8,12':
            return 'iPad Pro 12.9" 4G';
        case 'iPad11,6':
            return 'iPad 10.9" 8G';
        case 'iPad12,1':
            return 'iPad 10.9" 9G';
        case 'iPad13,8':
        case 'iPad13,9':
        case 'iPad13,10':
        case 'iPad13,11':
            return 'iPad Pro 12.9" 5G';
        case 'iPad13,18':
            return 'iPad 10.9" 10G';
        case 'iPad14,5':
            return 'iPad Pro 12.9" 6G';
        default:
            return null;
    }
}
// Don't specify the return type so that we can infer with ReturnType<> and use as an "enum" in
// deviceTypeFromModelName.
// oxlint-disable-next-line typescript/explicit-function-return-type
function nameFromAndroidModelName(modelName, webViewVersion) {
    // Only true Elo i3 devices uses chrome.
    // Elo i2 devices on Android 8.1 and Elo i4 devices use com.google.android.webview
    const isUsingChrome = (webViewVersion || '').startsWith('com.android.chrome@');
    // TODO: map the Samsung KM24A Flytech box's ro.product.model once a unit reports in
    // (bench enrollment is currently blocked on a serial issue); until then
    // AndroidSamsungKM24AFlytech kiosks report the UnhandledModel warning.
    switch (modelName) {
        case 'Elo-i2-15Std':
            return 'Elo i2 15"';
        case 'Elo-i2-22Std':
            return 'Elo i2 22"';
        case 'Elo-i2-Puck':
            return 'Elo i2 Backpack';
        case 'Elo-i3-15Std':
            return isUsingChrome ? 'Elo i3 15"' : 'Elo i2 15"';
        case 'Elo-i3-22Std':
            return isUsingChrome ? 'Elo i3 22"' : 'Elo i2 22"';
        case 'Elo-i3-PUCK':
            return isUsingChrome ? 'Elo i3 Backpack' : 'Elo i2 Backpack';
        case '15in-I-Series-4':
            return 'Elo i4 15"';
        case '15in-I-Series-4-Value':
            return 'Elo i4 15" (Value)';
        case '22in-I-Series-4':
            return 'Elo i4 22"';
        case '22in-I-Series-4-Value':
            return 'Elo i4 22" (Value)';
        case '22in-I-Series-5':
            return 'Elo i5 22"';
        case 'Backpack-4':
            return 'Elo i4 Backpack';
        case 'Backpack-4-Value':
            return 'Elo i4 Backpack (Value)';
        case 'Backpack-5':
            return 'Elo i5 Backpack';
        case 'x86_64':
            return 'Simulator';
        case 'Lenovo TB-X6C6F':
            return 'Lenovo Android K10';
        case '11AJS0JA00':
        case '11AMS0S800':
            return 'Lenovo Android M90n';
        case 'Android x86':
            return 'Lenovo Android (x86)';
        case 'igs-series-gen2':
        case 'IDC_Series':
            return 'MicroTouch Android 22"';
        case 'rk3399-all':
            return 'Pyramid Flex Android 21.5"';
        case 'ET40':
            return 'Zebra ET40';
        case 'QK22':
            return 'Touch Dynamic QK22';
        case 'SM-X210':
            return 'Samsung Galaxy Tab A9';
        case 'SM-T540':
            return 'Samsung Galaxy Tab Active Pro 2019';
        case 'SM-T970':
            return 'Samsung Galaxy Tab S7+';
        default:
            return null;
    }
}
// BITE-7194: Add full list of ChromeOS devices once we get proper chromeos device information
// Don't specify the return type so that we can infer with ReturnType<> and use as an "enum" in
// deviceTypeFromModelName.
// oxlint-disable-next-line typescript/explicit-function-return-type
function nameFromChromeOSModelName(modelName) {
    switch (modelName) {
        case 'Samsung':
            return 'Samsung KM24W';
        case 'ChromeOS Device':
            return 'ChromeOS Device';
        default:
            return null;
    }
}
// Don't specify the return type so that we can infer with ReturnType<> and use as an "enum" in
// deviceTypeFromModelName.
// oxlint-disable-next-line typescript/explicit-function-return-type
function nameFromTizenModelName(modelName) {
    switch (modelName) {
        case 'Samsung':
            return 'Samsung KM24A';
        default:
            return null;
    }
}
/**
 * When we determine the device type of a device, we determine from its
 * modelName so that we can avoid some human errors. For example, the kiosk
 * device type could be set to AndroidElo22, and the actual device is an
 * iPad.
 *
 * Here, the reason we want the kioskDeviceType passed into the function is to
 * separate Elo 22" using Elo View or using the Workspace One.
 *
 * @param modelName The model name of the device.
 * @param kioskDeviceType The kiosk assigned device type (optional).
 * @returns The device type we think it is.
 */
export function deviceTypeFromModelName(modelName, kioskDeviceType) {
    switch (modelName) {
        case 'ChromeOS Device':
            return KioskDeviceType.ChromeOsSamsungKM24W;
        case 'Elo i2 15"':
        case 'Elo i3 15"':
        case 'Elo i4 15"':
            return KioskDeviceType.AndroidElo15;
        case 'Elo i2 22"':
        case 'Elo i3 22"':
        case 'Elo i4 22"':
        case 'Elo i4 22" (Value)':
        case 'Elo i5 22"':
        case 'Elo i4 15" (Value)':
            switch (kioskDeviceType) {
                case KioskDeviceType.AndroidElo22WorkspaceOne:
                    return KioskDeviceType.AndroidElo22WorkspaceOne;
                default:
                    return KioskDeviceType.AndroidElo22;
            }
        case 'Elo i2 Backpack':
        case 'Elo i3 Backpack':
        case 'Elo i4 Backpack':
        case 'Elo i4 Backpack (Value)':
        case 'Elo i5 Backpack':
            switch (kioskDeviceType) {
                case KioskDeviceType.AndroidEloBackpackWorkspaceOne:
                    return KioskDeviceType.AndroidEloBackpackWorkspaceOne;
                default:
                    return KioskDeviceType.AndroidElo22Backpack;
            }
        case 'iPad Air 2':
        case 'iPad 10.9" 7G':
        case 'iPad 10.9" 8G':
        case 'iPad 10.9" 9G':
        case 'iPad 10.9" 10G':
            return KioskDeviceType.iPad;
        case 'iPad Pro 12.9"':
        case 'iPad Pro 12.9" 2G':
        case 'iPad Pro 12.9" 3G':
        case 'iPad Pro 12.9" 4G':
        case 'iPad Pro 12.9" 5G':
        case 'iPad Pro 12.9" 6G':
            return KioskDeviceType.iPadPro;
        case 'Lenovo Android K10':
        case 'Lenovo Android (x86)':
            return KioskDeviceType.AndroidLenovoK10;
        case 'Lenovo Android M90n':
            return KioskDeviceType.AndroidLenovoM90n;
        case 'MicroTouch Android 22"':
            return KioskDeviceType.AndroidMicroTouch22;
        case 'Pyramid Flex Android 21.5"':
            return KioskDeviceType.AndroidPyramidFlex215;
        case 'Samsung Galaxy Tab A9':
            return KioskDeviceType.AndroidSamsungGalaxyTabASeries;
        case 'Samsung Galaxy Tab Active Pro 2019':
            return KioskDeviceType.AndroidSamsungGalaxyTabActiveProSeries;
        case 'Samsung Galaxy Tab S7+':
            return KioskDeviceType.AndroidSamsungGalaxyTabSSeries;
        case 'Samsung KM24A':
            return KioskDeviceType.TizenSamsungKM24A;
        case 'Samsung KM24W':
            return KioskDeviceType.ChromeOsSamsungKM24W;
        case 'Touch Dynamic QK22':
            return KioskDeviceType.AndroidTouchDynamic;
        case 'Zebra ET40':
            return KioskDeviceType.AndroidZebraET40;
        case 'Simulator':
        case null:
            return null;
    }
}
export var KioskWarning;
(function (KioskWarning) {
    KioskWarning["OutdatedBuild"] = "outdated-build";
    KioskWarning["OutdatedOs"] = "outdated-os";
    KioskWarning["OutdatedWebView"] = "outdated-webview";
    KioskWarning["UnhandledModel"] = "unhandled-model";
    KioskWarning["WrongDpi"] = "wrong-dpi";
    KioskWarning["TouchDisplayFaulty"] = "touch-display-faulty";
})(KioskWarning || (KioskWarning = {}));
export function parseDeviceStatus(deviceStatus, kioskDeviceType) {
    const { buildName, buildNumber, modelName = '', os, osVersion, webViewVersion, displayStatus, } = deviceStatus;
    const summaryParts = [];
    if (deviceStatus.garconBuildName) {
        summaryParts.push(buildName ? `Kiosk ${buildName}` : `Kiosk #${buildNumber}`);
        summaryParts.push(`Garcon ${deviceStatus.garconBuildName}`);
    }
    else {
        summaryParts.push(buildName ? `Build ${buildName}` : `Build #${buildNumber}`);
    }
    const warnings = [];
    let displayModelName = null;
    if (os === KioskOs.Android) {
        if (buildName) {
            // substring(1) to remove the leading v in 'v1.x' if it exists
            let buildVersion = buildName.startsWith('v') ? buildName.substring(1) : buildName;
            const canarySeparatorIndex = buildVersion.indexOf('-');
            if (canarySeparatorIndex >= 0) {
                // Remove the canary release number at the end of the build name
                buildVersion = buildVersion.slice(0, canarySeparatorIndex);
            }
            if (buildVersion && semver.valid(buildVersion)) {
                const isV2App = semver.gte(buildVersion, '2.0.0');
                if (isV2App) {
                    if (semver.lt(buildVersion, LATEST_GA_ANDROID_V2_BUILD_VERSION)) {
                        warnings.push({
                            code: KioskWarning.OutdatedBuild,
                            message: 'Newer v2 APK needs to be installed',
                        });
                    }
                }
                else {
                    if (semver.lt(buildVersion, LATEST_GA_ANDROID_V1_BUILD_VERSION)) {
                        warnings.push({
                            code: KioskWarning.OutdatedBuild,
                            message: 'Newer v1 APK needs to be installed',
                        });
                    }
                }
            }
        }
        displayModelName = nameFromAndroidModelName(modelName, webViewVersion);
        if (!displayModelName) {
            warnings.push({
                code: KioskWarning.UnhandledModel,
                message: 'Model name needs to be parsed',
            });
        }
        const deviceType = deviceTypeFromModelName(displayModelName, kioskDeviceType);
        const isEloAndroid = deviceType !== null &&
            [MdmProvider.EloView, MdmProvider.WorkspaceOne].includes(KioskDeviceTypeHelper.getMdmProvider(deviceType));
        summaryParts.push(displayModelName || modelName);
        summaryParts.push(`Android ${osVersion}`);
        const osVersionMajor = Number(osVersion.match(/([0-9]+)/)[0]); // 7, 8, 10
        let webViewVersionNumberMajor = 0;
        if (webViewVersion) {
            summaryParts.push(`WebView ${webViewVersion}`);
            webViewVersionNumberMajor = Number(webViewVersion.match(/@([0-9]+)/)[1]);
        }
        if (displayStatus && deviceType) {
            const { density, eloDeviceDpi } = displayStatus;
            const dpiThreshold = getKioskDisplayDpiWarningThreshold(deviceType);
            if (displayStatus.density >= dpiThreshold) {
                warnings.push({
                    code: KioskWarning.WrongDpi,
                    message: 'Wrong DPI. Please change in Android Settings to 160 DPI and reboot the device.',
                });
            }
            else if (displayStatus.deviceRebootRequired) {
                warnings.push({
                    code: KioskWarning.WrongDpi,
                    message: 'Wrong DPI. Please reboot the device.',
                });
            }
            summaryParts.push(`Density: ${density}`);
            if (eloDeviceDpi) {
                summaryParts.push(`DPI ${eloDeviceDpi}`);
            }
        }
        /**
         * If it's really an i2 device (is AOSP), then we expect it to have
         * com.google.android.webview@85.x (which comes natively with Android 8 and Elo Software
         * 4.58.20+a. i2 devices on Android 7 can have at most com.android.webview@74.x and they
         * NEED to get upgraded to 4.58.20+a to get Android 8.1 and webview@85.
         *
         * There's now a new way to get i2 devices upgraded to Chrome 94:
         * https://www.notion.so/getbite/Update-Elo-WebView-Version-Via-EloView-996121ef2dd646abacf2d2b6dec46b05?pvs=4
         *
         * If it's really an i3 device (has GMS), then we expect it to have chrome installed and it
         * should be at least com.android.chrome@97.x. GMS i3 devices should auto-update chrome
         * without our involvement.
         *
         * i4 devices (or Android 10) come with webview com.google.android.webview@99.x and they
         * also have an auto-update mechanism for the webview.
         */
        if (osVersionMajor === 7) {
            if (isEloAndroid) {
                warnings.push({
                    code: KioskWarning.OutdatedOs,
                    message: 'Update Elo Software to 4.+ to get Android 8.1',
                });
                warnings.push({
                    code: KioskWarning.OutdatedWebView,
                    message: 'Outdated WebView. OS update needed.',
                });
            }
            else if (webViewVersionNumberMajor < 72) {
                warnings.push({
                    code: KioskWarning.OutdatedWebView,
                    message: 'Outdated WebView. Please update it through Esper.',
                });
            }
        }
        else if (osVersionMajor === 8 || osVersionMajor === 9) {
            if (isEloAndroid) {
                if (webViewVersion?.startsWith('com.google.android.webview@')) {
                    // This is an Elo i2 device
                    if (webViewVersionNumberMajor < 94) {
                        warnings.push({
                            code: KioskWarning.OutdatedWebView,
                            message: 'i2 WebView is outdated. Update using <a href="https://www.notion.so/getbite/Update-Elo-WebView-Version-Via-EloView-996121ef2dd646abacf2d2b6dec46b05?pvs=4" target="_blank">these instructions</a>.',
                        });
                    }
                }
                else {
                    // This is an Elo i3 device
                    if (webViewVersionNumberMajor < 97) {
                        warnings.push({
                            code: KioskWarning.OutdatedWebView,
                            message: 'i3 WebView is outdated. Update Elo software or do an enterprise reset.',
                        });
                    }
                }
            }
            else if (webViewVersionNumberMajor < 94) {
                // This is an Esper or WorkspaceOne device
                warnings.push({
                    code: KioskWarning.OutdatedWebView,
                    message: 'Outdated WebView. Please update it through Esper.',
                });
            }
        }
        else if ([10, 11].includes(osVersionMajor)) {
            if (webViewVersionNumberMajor < 100) {
                warnings.push({
                    code: KioskWarning.OutdatedWebView,
                    message: 'Outdated WebView',
                });
            }
        }
    }
    else if (os === KioskOs.iOS) {
        // substring(1) to remove the leading v in 'v1.x' if it exists
        let buildVersion = buildName?.split('')[0] === 'v' ? buildName.substring(1) : buildName;
        // iOS build version doesn't have the patch component. Force it to be 0.
        if (!buildVersion ||
            (buildVersion && semver.lt(`${buildVersion}.0`, `${LATEST_GA_IOS_BUILD_VERSION}.0`))) {
            warnings.push({
                code: KioskWarning.OutdatedBuild,
                message: 'Newer build needs to be installed',
            });
        }
        displayModelName = nameFromIosModelName(modelName);
        if (!displayModelName) {
            warnings.push({
                code: KioskWarning.UnhandledModel,
                message: 'Model name needs to be parsed',
            });
        }
        summaryParts.push(displayModelName || modelName);
        summaryParts.push(`iOS ${osVersion}`);
        const osVersionMajor = Number(osVersion.match(/([0-9]+)/)[0]);
        if (osVersionMajor < 16) {
            warnings.push({
                code: KioskWarning.OutdatedOs,
                message: 'Update iOS to at least v16.x',
            });
        }
    }
    else if (os === KioskOs.Tizen) {
        displayModelName = nameFromTizenModelName(modelName);
        if (!displayModelName) {
            warnings.push({
                code: KioskWarning.UnhandledModel,
                message: 'Model name needs to be parsed',
            });
        }
        summaryParts.push(displayModelName || modelName);
        summaryParts.push(`${KioskOs.Tizen} ${osVersion}`);
    }
    else if (os === KioskOs.ChromeOS) {
        displayModelName = nameFromChromeOSModelName(modelName);
        if (!displayModelName) {
            warnings.push({
                code: KioskWarning.UnhandledModel,
                message: 'Model name needs to be parsed',
            });
        }
        summaryParts.push(displayModelName || modelName);
        summaryParts.push(`${KioskOs.ChromeOS} ${osVersion}`);
    }
    else {
        summaryParts.push(`Unknown OS (${modelName})`);
        summaryParts.push(modelName);
        warnings.push({
            code: KioskWarning.UnhandledModel,
            message: 'Model name and OS need to be parsed',
        });
    }
    return { displayModelName, statusSummary: summaryParts.join(', '), warnings };
}
/**
 * Extracts the semantic version from a build name. Note that the result is
 * not guaranteed to be in a valid semantic version format. For example, v3.17
 * or 399 will never be a valid semantic version even after extraction.
 *
 * @param buildName - The build name from which to extract the semantic version.
 *                    Example: 1.16.0, v1.16.0, v1.16.0-android12.01, v3.17, 399
 * @returns The extracted semantic version.
 */
function extractSemver(buildName) {
    const canarySeparatorIndex = buildName.indexOf('-');
    const version = canarySeparatorIndex < 0 ? buildName : buildName.slice(0, canarySeparatorIndex);
    return version.replace(/^v/, '');
}
export function kioskSupportsAppLogin(deviceStatus) {
    switch (deviceStatus.os) {
        case KioskOs.Android:
            const buildName = deviceStatus.buildName;
            if (!buildName) {
                // Some very old versions might be missing buildName
                throw new Error(`Missing buildName for kiosk`);
            }
            return (VersioningHelper.semverCompare(extractSemver(buildName), '2.0.0')?.isGreaterVersion ?? false);
        case KioskOs.iOS:
            return false;
        case KioskOs.ChromeOS:
        case KioskOs.Tizen:
            return false;
    }
    return false; // for unrecognized os.
}
export function kioskSupportsAppLogout(deviceStatus) {
    switch (deviceStatus.os) {
        case KioskOs.Android:
            const buildName = deviceStatus.buildName;
            if (!buildName) {
                // Some very old versions might be missing buildName
                throw new Error(`Missing buildName for kiosk`);
            }
            const isV2App = semver.gte(extractSemver(buildName), '2.0.0');
            if (isV2App) {
                // Garcon doesn't support app logout at all
                return false;
            }
            return (VersioningHelper.semverCompare(extractSemver(buildName), '1.16.8')?.isGreaterVersion ??
                false);
        case KioskOs.ChromeOS:
        case KioskOs.iOS:
        case KioskOs.Tizen:
            return false;
    }
    return false; // for unrecognized os.
}
/**
 * Live View drives the kiosk remotely: native touch injection needs the v2.20.0+ Android app,
 * and remote gesture handling in the WebView needs garcon 0.18.2+ (garconBuildName looks like
 * `biteinc-garcon.0.18.2`). Returns false rather than throwing on missing/unparseable versions —
 * this backs menu visibility, not an action.
 */
export function kioskSupportsLiveView(deviceStatus) {
    if (deviceStatus.os !== KioskOs.Android) {
        return false;
    }
    const appVersion = deviceStatus.buildName ? extractSemver(deviceStatus.buildName) : null;
    const garconVersion = deviceStatus.garconBuildName?.match(/\d+\.\d+\.\d+/)?.[0] ?? null;
    // The regex only checks the shape — semver.gte throws on invalid versions like `01.18.2`, so
    // both extracted versions must pass semver.valid.
    if (!appVersion || !semver.valid(appVersion) || !garconVersion || !semver.valid(garconVersion)) {
        return false;
    }
    return semver.gte(appVersion, '2.20.0') && semver.gte(garconVersion, '0.18.2');
}
/**
 * Only report DPI warnings for Elo devices, as only these devices provide a DPI setting
 * directly within the display settings. For all other devices, the default is not so bad
 * and default scales to 1.5.
 * @param deviceType the device type of the kiosk.
 * @returns the DPI threshold for the device to show a warning.
 */
function getKioskDisplayDpiWarningThreshold(deviceType) {
    switch (deviceType) {
        case KioskDeviceType.AndroidElo15:
        case KioskDeviceType.AndroidElo22:
        case KioskDeviceType.AndroidElo22Backpack:
        case KioskDeviceType.AndroidElo22WorkspaceOne:
        case KioskDeviceType.AndroidElo32Backpack:
        case KioskDeviceType.AndroidEloBackpackWorkspaceOne:
            return 1.5;
        case KioskDeviceType.AndroidHPEngagePro19_5:
        case KioskDeviceType.AndroidHPEngagePro24:
        case KioskDeviceType.AndroidLenovoK10:
        case KioskDeviceType.AndroidLenovoM90n:
        case KioskDeviceType.AndroidMicroTouch22:
        case KioskDeviceType.AndroidPosiflexMercury:
        case KioskDeviceType.AndroidPyramidFlex215:
        case KioskDeviceType.AndroidFlytechAcrelec:
        case KioskDeviceType.AndroidSamsungGalaxyTabActiveProSeries:
        case KioskDeviceType.AndroidSamsungGalaxyTabASeries:
        case KioskDeviceType.AndroidSamsungGalaxyTabSSeries:
        case KioskDeviceType.AndroidSamsungKM24AFlytech:
        case KioskDeviceType.AndroidSunmi215:
        case KioskDeviceType.AndroidTouchDynamic:
        case KioskDeviceType.AndroidZebraET40:
        case KioskDeviceType.ChromeOsAcrelecK22:
        case KioskDeviceType.ChromeOsElo15:
        case KioskDeviceType.ChromeOsElo22:
        case KioskDeviceType.ChromeOsHPEngagePro19_5:
        case KioskDeviceType.ChromeOsHPEngagePro24:
        case KioskDeviceType.ChromeOsLenovoChromeBox:
        case KioskDeviceType.ChromeOsLenovoM90n:
        case KioskDeviceType.ChromeOsLG27KC3PK_C:
        case KioskDeviceType.ChromeOsMicroTouch22:
        case KioskDeviceType.ChromeOsNcr7360:
        case KioskDeviceType.ChromeOsPosiflexMercury:
        case KioskDeviceType.ChromeOsPyramidPassport32:
        case KioskDeviceType.ChromeOsSamsungKM24ALenovoChromeBox:
        case KioskDeviceType.ChromeOsSamsungKM24W:
        case KioskDeviceType.ChromeOsTouchDynamicEdgeUltra22:
        case KioskDeviceType.iPad:
        case KioskDeviceType.iPadPro:
        case KioskDeviceType.TizenSamsungKM24A:
            return 2.0;
    }
}
export function getBitePlatform(kiosk) {
    const mdmProvider = KioskDeviceTypeHelper.getMdmProvider(kiosk.deviceType);
    switch (mdmProvider) {
        case MdmProvider.ChromeOSFlex:
            return BitePlatform.KioskChromeOsGarcon;
        case MdmProvider.EloView:
        case MdmProvider.Esper:
        case MdmProvider.WorkspaceOne:
            if (kiosk.status?.deviceStatus.buildName?.startsWith('2.')) {
                return BitePlatform.KioskAndroidGarcon;
            }
            return BitePlatform.KioskAndroid;
        case MdmProvider.SignageOS:
            return BitePlatform.KioskSignageOsGarcon;
        case MdmProvider.SimpleMDM:
            return BitePlatform.KioskIos;
    }
}
export function usesGarcon(kiosk) {
    return BitePlatformHelper.usesGarcon(getBitePlatform(kiosk));
}
//# sourceMappingURL=kiosk_helper.js.map